import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import { ErrorAlert } from 'smartsuite-ui/lib/alerts/ErrorAlert';
import { inputTypes } from '../shared/constants/constants';
import FilterSingleSelect from '../shared/filtering/FilterSingleSelect';
import FilterMultiSelect from '../shared/filtering/FilterMultiSelect';
import FilterUnconstrainedMultiSelect from '../shared/filtering/FilterUnconstrainedMultiSelect';
import FilterTreeSelect from '../shared/filtering/FilterTreeSelect';
import {
    getResetCascadeChildFilter,
    shouldShowFilter,
    getFilterOptions,
} from '../shared/filtering/filtersModalUtils';

import { removeFromArray } from '../viewDashboards/widgets/utils/dataFormatUtils';
import { AdditionalFilterSelector } from '../shared/filtering/AdditionalFilterSelector';

import WidgetLoading from '../shared/WidgetLoading';
import { useLabelContext } from '../shared/hooks/labelContext';

/* eslint-disable complexity */
export default function EditorFilters({
    availableFilters,
    filterValues,
    isLoadingSelectOptions,
    requiredFilterKeys,
    saveFilters,
    selectOptions,
    showRequiredFilterNoSelectOptionsError,
}) {
    const { dashboardLabels, predicateLabels } = useLabelContext();
    const additionalFilterKeys = Object.keys(availableFilters).filter(
        (x) => !requiredFilterKeys.includes(x)
    );

    const [usedAdditionalFilterKeys, setUsedAdditionalFilterKeys] = useState(
        additionalFilterKeys.filter((filterKey) =>
            keys(filterValues).includes(filterKey)
        )
    );
    function handleAdditionalFilterSelect(selectedFilterOption) {
        if (selectedFilterOption) {
            setUsedAdditionalFilterKeys((prevState) => [
                ...prevState,
                selectedFilterOption.valueField,
            ]);
        }
    }

    function removeAdditionalFilter(filterKey) {
        return () => {
            const updatedFilterValues = { ...filterValues };
            delete updatedFilterValues[filterKey];

            saveFilters({
                ...updatedFilterValues,
                ...getResetCascadeChildFilter(
                    availableFilters[filterKey].cascadeChildKey,
                    updatedFilterValues,
                    predicateLabels
                ),
            });

            setUsedAdditionalFilterKeys((prevState) => {
                const updatedUsedFilterKeys = removeFromArray(
                    prevState,
                    prevState.indexOf(filterKey)
                );

                if (availableFilters[filterKey].cascadeChildKey) {
                    return removeFromArray(
                        updatedUsedFilterKeys,
                        updatedUsedFilterKeys.indexOf(
                            availableFilters[filterKey].cascadeChildKey
                        )
                    );
                }

                return updatedUsedFilterKeys;
            });
        };
    }

    function updateFilter(updatedFilter, cascadeChildKey) {
        saveFilters({
            ...filterValues,
            ...updatedFilter,
            ...getResetCascadeChildFilter(
                cascadeChildKey,
                filterValues,
                predicateLabels
            ),
        });
    }

    function buildFilterComponent(filterKey, isAdditionalFilter) {
        const filter = availableFilters[filterKey];

        if (!filter) {
            return null;
        }

        const isRequired = requiredFilterKeys.includes(filterKey);
        const isAdditionalFilterUsed =
            usedAdditionalFilterKeys.includes(filterKey);
        const options = getFilterOptions(
            availableFilters,
            filterKey,
            filterValues,
            selectOptions,
            requiredFilterKeys
        );

        if (
            !shouldShowFilter(filter, options, isRequired) ||
            (isAdditionalFilter && !isAdditionalFilterUsed)
        ) {
            return null;
        }

        const selectProps = {
            key: filterKey,
            filter: filterValues[filterKey],
            availableFilter: { ...filter, appliesToDataSubset: false },
            disabled: false,
            filterKey,
            isAdditionalFilter,
            isRequired,
            isEditor: true,
            options,
            showAsterisk: true,
            removeAdditionalFilter,
            updateFilter,
        };

        if (filter.type === inputTypes.singleSelect) {
            return <FilterSingleSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.multiSelect) {
            return <FilterMultiSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.unconstrainedMultiSelect) {
            return <FilterUnconstrainedMultiSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.treeSelect) {
            return <FilterTreeSelect {...selectProps} />;
        }

        return null;
    }

    function buildRequiredFilterComponent(filterKey) {
        return buildFilterComponent(filterKey, false);
    }
    function buildAdditionalFilterComponent(filterKey) {
        return buildFilterComponent(filterKey, true);
    }

    if (isEmpty(availableFilters)) {
        return null;
    }

    const requiredFilterComponents = requiredFilterKeys.map(
        (requiredFilterKey) => (
            <div key={requiredFilterKey}>
                <hr className="divider" />
                {buildRequiredFilterComponent(requiredFilterKey)}
            </div>
        )
    );
    const additionalFilterComponents = usedAdditionalFilterKeys.map(
        (usedAdditionalFilterKey) => (
            <div key={usedAdditionalFilterKey}>
                <hr className="divider" />
                {buildAdditionalFilterComponent(usedAdditionalFilterKey)}
            </div>
        )
    );

    const populatedAdditionalFilterKeys = additionalFilterKeys.filter(
        (filterKey) =>
            shouldShowFilter(
                availableFilters[filterKey],
                getFilterOptions(
                    availableFilters,
                    filterKey,
                    filterValues,
                    selectOptions,
                    requiredFilterKeys
                ),
                requiredFilterKeys.includes(filterKey)
            )
    );

    if (isLoadingSelectOptions) {
        return (
            <div className="widget-body--loading">
                <WidgetLoading noOverlayBackground={true} />
            </div>
        );
    }

    if (showRequiredFilterNoSelectOptionsError) {
        return (
            <ErrorAlert>
                {dashboardLabels.errorGettingWidgetFilterOptions}
            </ErrorAlert>
        );
    }

    if (
        isEmpty(requiredFilterComponents) &&
        !populatedAdditionalFilterKeys.length
    ) {
        return null;
    }

    return (
        <div>
            {requiredFilterComponents}
            {additionalFilterComponents}
            <hr className="divider" />
            <AdditionalFilterSelector
                additionalFilterKeys={populatedAdditionalFilterKeys}
                availableFilters={availableFilters}
                handleAdditionalFilterSelect={handleAdditionalFilterSelect}
                isEditor={true}
                selectOptions={selectOptions}
                usedAdditionalFilterKeys={usedAdditionalFilterKeys}
            />
        </div>
    );
}
/* eslint-enable complexity */

EditorFilters.propTypes = {
    availableFilters: PropTypes.instanceOf(Object).isRequired,
    filterValues: PropTypes.instanceOf(Object).isRequired,
    isLoadingSelectOptions: PropTypes.bool.isRequired,
    requiredFilterKeys: PropTypes.instanceOf(Array).isRequired,
    saveFilters: PropTypes.func.isRequired,
    selectOptions: PropTypes.instanceOf(Array),
    setSelectOptions: PropTypes.func.isRequired,
    showRequiredFilterNoSelectOptionsError: PropTypes.bool.isRequired,
};
